/**
 * Invite User Modal Component
 * Modal for sending user invitations via email
 */

import { useState } from 'react';
import { useCreateInvitation } from '@/hooks/useInvitations';
import { toast } from 'sonner';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface InviteUserModalProps {
  onClose: () => void;
}

const InviteUserModal = ({ onClose }: InviteUserModalProps) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'employee'>('employee');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const createInvitationMutation = useCreateInvitation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email.trim()) {
      setError('Email адресът е задължителен');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Невалиден email формат');
      return;
    }

    try {
      await createInvitationMutation.mutateAsync({
        email: email.trim(),
        role,
      });
      setSuccess(true);
      setEmail('');
      toast.success('Поканата е изпратена успешно!');
      // Keep modal open to show success and allow sending more invitations
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Грешка при изпращане на покана';
      setError(errorMessage);
      
      // Check if it's an SMTP error and show global toast notification
      if (errorMessage.includes('SMTP') || errorMessage.includes('Email send failed')) {
        toast.error('SMTP грешка: Проблем с email сървъра', {
          description: 'Моля, уведомете администратора. Email сървърът е недостъпен или неправилно конфигуриран.',
          duration: 8000,
        });
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Покани нов потребител
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Success message */}
          {success && (
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-400">
                ✓ Поканата е изпратена успешно!
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Email Input */}
          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
            >
              Email адрес *
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
                setSuccess(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              placeholder="ivan@example.com"
              autoFocus
            />
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Роля
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="employee"
                  checked={role === 'employee'}
                  onChange={() => setRole('employee')}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                  Служител
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={role === 'admin'}
                  onChange={() => setRole('admin')}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                  Администратор
                </span>
              </label>
            </div>
          </div>

          {/* Info text */}
          <p className="text-xs text-gray-500 dark:text-dark-text-muted">
            На посочения email ще бъде изпратена покана с линк за регистрация. Поканата е валидна 7 дни.
          </p>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-bg-primary rounded-lg transition-colors"
          >
            Затвори
          </button>
          <button
            onClick={handleSubmit}
            disabled={createInvitationMutation.isPending || !email.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {createInvitationMutation.isPending ? 'Изпращане...' : 'Изпрати покана'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
